import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LangTabs, useLangForm } from '../../components/ui/lang-tabs';
import { toast } from 'sonner';
import { Building, Save } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_FIELDS = [
  'company_name', 'representative', 'established', 'capital',
  'license_number', 'address', 'business_description',
];

const fields = [
  { key: 'company_name', type: 'text', localizable: true },
  { key: 'representative', type: 'text', localizable: true },
  { key: 'established', type: 'text', localizable: true },
  { key: 'capital', type: 'text', localizable: true },
  { key: 'license_number', type: 'text', localizable: true },
  { key: 'address', type: 'text', localizable: true },
  { key: 'phone', type: 'text', localizable: false },
  { key: 'email', type: 'email', localizable: false },
  { key: 'website', type: 'url', localizable: false },
  { key: 'business_description', type: 'textarea', localizable: true },
];

export default function AdminCompanyPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const { lang, setLang, getField, setField } = useLangForm(form, LANG_FIELDS);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/settings/company`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      const { key, updated_at, updated_by, ...data } = res.data;
      setForm(data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings/company`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
      toast.success(t('company_saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('company_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout><div className="text-center py-12 text-muted-foreground">{t('loading')}</div></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-company-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
              <Building className="h-8 w-8" />
              {t('company_admin_title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('company_admin_subtitle')}</p>
          </div>
          <LangTabs activeLang={lang} onChange={setLang} />
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fields.map(({ key, type, localizable }) => (
              <div key={key} className={`space-y-2 ${type === 'textarea' ? 'md:col-span-2' : ''}`}>
                <Label htmlFor={key}>
                  {t(`company_${key}`)}
                  {localizable && lang !== 'ja' && <span className="text-xs text-muted-foreground ml-2">({lang.toUpperCase()})</span>}
                </Label>
                {type === 'textarea' ? (
                  <textarea
                    id={key}
                    value={localizable ? getField(form, key) : (form[key] || '')}
                    onChange={(e) => localizable ? setField(form, setForm, key, e.target.value) : setForm({...form, [key]: e.target.value})}
                    rows={4}
                    className="w-full rounded-lg border border-border bg-background px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
                    data-testid={`company-${key}-input`}
                  />
                ) : (
                  <Input
                    id={key}
                    type={type}
                    value={localizable ? getField(form, key) : (form[key] || '')}
                    onChange={(e) => localizable ? setField(form, setForm, key, e.target.value) : setForm({...form, [key]: e.target.value})}
                    data-testid={`company-${key}-input`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 flex gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-company-btn">
              <Save className="h-4 w-4" />
              {saving ? t('admin_settings_saving') : t('company_save_btn')}
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
